'use client'

import { useCallback, useEffect, useRef, useState, useContext } from 'react'
import { isEmpty } from 'lodash'
import { Loader } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  CoordinatesModel,
  CountryBoundsModel,
} from '@marketplace-web/shipping/checkout-shipping-data'
import {
  DropOffPointSource,
  useDropOffPointMapContext,
  GetShippingLabelContext,
} from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import {
  viewEvent,
  DropOffPointModel,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { transformGoogleLatLngCoordinates } from '@marketplace-web/shipping/google-maps-data'
import { GoogleMap } from '@marketplace-web/shipping/google-maps-feature'

import {
  DropOffPointMarker,
  AddressMarker,
  Controls,
  CurrentLocation,
  SearchArea,
} from './components'

const MAP_DEFAULT_ZOOM = 4
const MAP_MIN_ZOOM = 3
const MAP_SINGLE_CLICK_DELAY = 200
const NO_POINTS_ZOOM_OUT_RADIUS = 15000

type Props = {
  googleApiKey: string
  addressCoordinates: CoordinatesModel | null
  onMapClick: () => void
  countryBounds?: CountryBoundsModel
}

const DropOffPointMap = ({
  googleApiKey,
  addressCoordinates,
  onMapClick,
  countryBounds,
}: Props) => {
  const { track } = useTracking()
  const {
    uiState,
    selectedDropOffPoint,
    nearbyDropOffPoints,
    dropOffPointSource,
    fetchNearbyDropOffPoints,
    selectDropOffPoint,
  } = useDropOffPointMapContext()
  const {
    state: {
      labelTypeSelection: { selectedLabelType },
    },
  } = useContext(GetShippingLabelContext)

  const transaction = useConversationContext().conversation?.transaction

  const transactionId = transaction?.id
  const shipmentStatus = transaction?.shipment?.status

  const singleMapClickTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [map, setMap] = useState<google.maps.Map>()
  const [location, setLocation] = useState<CoordinatesModel | null>(null)
  const [showSearchArea, setShowSearchArea] = useState(false)

  const getBounds = useCallback(() => {
    const bounds = new google.maps.LatLngBounds()

    if (!nearbyDropOffPoints?.length) return null

    nearbyDropOffPoints.forEach(dropOffPoint => {
      const latitude = Number(dropOffPoint.latitude)
      const longitude = Number(dropOffPoint.longitude)
      bounds.extend(transformGoogleLatLngCoordinates({ latitude, longitude }))
    })

    return bounds
  }, [nearbyDropOffPoints])

  const getInitialBounds = useCallback(() => {
    const bounds = new google.maps.LatLngBounds()

    const addPointsToBounds = points => {
      points.forEach(point => {
        const latitude = Number(point.latitude)
        const longitude = Number(point.longitude)
        bounds.extend(transformGoogleLatLngCoordinates({ latitude, longitude }))
      })
    }

    if (!addressCoordinates || !nearbyDropOffPoints?.length) return null

    const nearestDropOffPoint = nearbyDropOffPoints[0]!

    const selectedDropOffPointCoordinates = {
      latitude: Number(nearestDropOffPoint.latitude),
      longitude: Number(nearestDropOffPoint.longitude),
    }

    const points = [addressCoordinates, selectedDropOffPointCoordinates]
    addPointsToBounds(points)

    return bounds
  }, [addressCoordinates, nearbyDropOffPoints])

  const fitBounds = useCallback(() => {
    const bounds = getInitialBounds()

    if (!bounds) return

    map?.fitBounds(bounds)
  }, [getInitialBounds, map])

  const bindListeners = useCallback(() => {
    if (!map) return undefined

    google.maps.event.addDomListenerOnce(map, 'idle', () => {
      google.maps.event.addDomListener(window, 'resize', () => {
        fitBounds()
      })

      google.maps.event.addListener(map, 'dragend', () => {
        setShowSearchArea(true)
      })
      google.maps.event.addListenerOnce(map, 'tilesloaded', () => {
        google.maps.event.addListener(map, 'zoom_changed', () => {
          setShowSearchArea(true)
        })
      })

      google.maps.event.addListener(map, 'click', () => {
        singleMapClickTimeout.current = setTimeout(onMapClick, MAP_SINGLE_CLICK_DELAY)
      })

      google.maps.event.addListener(map, 'dblclick', () => {
        if (!singleMapClickTimeout.current) return

        clearTimeout(singleMapClickTimeout.current)
      })
    })

    return () => {
      google.maps.event.clearListeners(window, 'resize')
      google.maps.event.clearListeners(map, 'dragend')
      google.maps.event.clearListeners(map, 'zoom_changed')
      google.maps.event.clearListeners(map, 'click')
      google.maps.event.clearListeners(map, 'dblclick')
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, fitBounds])

  useEffect(() => {
    function initializeBounds() {
      let bounds = getBounds()

      if (!bounds) {
        if (!countryBounds) return

        bounds = new window.google.maps.LatLngBounds(
          transformGoogleLatLngCoordinates(countryBounds.bottomLeft),
          transformGoogleLatLngCoordinates(countryBounds.topRight),
        )
      }

      map?.fitBounds(bounds)
    }

    initializeBounds()
    bindListeners()
  }, [map, getBounds, countryBounds, bindListeners])

  useEffect(() => {
    if (isEmpty(nearbyDropOffPoints) && !location && uiState === UiState.Success) {
      setShowSearchArea(true)

      return
    }

    if (uiState === UiState.Success) {
      setShowSearchArea(false)
    }
  }, [location, nearbyDropOffPoints, uiState])

  useEffect(() => {
    const trackViewMarkerEvent = () => {
      if (!nearbyDropOffPoints) return

      nearbyDropOffPoints.map((nearbyDropOffPoint: DropOffPointModel) =>
        track(
          viewEvent({
            screen: 'seller_drop_off_map',
            target: 'drop_off_point',
            targetDetails: JSON.stringify({
              transaction_id: transactionId,
              drop_off_point_code: nearbyDropOffPoint.code,
              distance_to_address: nearbyDropOffPoint.distance,
              distance_unit: nearbyDropOffPoint.distanceUnit,
              shipment_status: shipmentStatus,
              drop_off_points_source: dropOffPointSource,
            }),
          }),
        ),
      )
    }

    trackViewMarkerEvent()
  }, [dropOffPointSource, nearbyDropOffPoints, shipmentStatus, track, transactionId])

  const handleMapLoaded = (loadedMap: google.maps.Map) => {
    setMap(loadedMap)
  }

  const handleZoomIn = () => {
    if (!map) return

    const currentZoom = map.getZoom()

    if (!currentZoom) return

    map.setZoom(currentZoom + 1)
  }

  const handleZoomOut = () => {
    if (!map) return

    const currentZoom = map.getZoom()

    if (!currentZoom) return

    map.setZoom(currentZoom - 1)
  }

  const handleNearbyPointsFetchFailure = useCallback(() => {
    if (!map) return

    const bounds = new google.maps.Circle({
      center: map.getCenter(),
      radius: NO_POINTS_ZOOM_OUT_RADIUS,
    }).getBounds()

    if (!bounds) return

    map.fitBounds(bounds)
    setShowSearchArea(false)
  }, [map])

  useEffect(() => {
    function handleMapUiState() {
      if (uiState !== UiState.Failure) return

      if (location) {
        map?.setCenter(transformGoogleLatLngCoordinates(location))
      }

      handleNearbyPointsFetchFailure()
    }

    handleMapUiState()
  }, [handleNearbyPointsFetchFailure, uiState, location, map])

  const handleLocation = (coordinates: CoordinatesModel) => {
    fetchNearbyDropOffPoints({
      coordinates,
      dropOffPointSource: DropOffPointSource.CurrentLocation,
      selectedLabelType,
    })
  }

  const handleLocationResponse = (coordinates: CoordinatesModel) => {
    setLocation(coordinates)
    setShowSearchArea(false)

    handleLocation(coordinates)
  }

  const handleSearchAreaClick = () => {
    const center = map?.getCenter()

    if (!center) return

    setShowSearchArea(false)

    fetchNearbyDropOffPoints({
      coordinates: {
        latitude: center.lat(),
        longitude: center.lng(),
      },
      dropOffPointSource: DropOffPointSource.SearchAreaButton,
      selectedLabelType,
    })
  }

  const setSelectedPoint = (newSelectedDropOffPoint?: DropOffPointModel | null) => {
    if (!newSelectedDropOffPoint) return

    selectDropOffPoint(newSelectedDropOffPoint)
  }

  const handleMarkerClick = (nearbyDropOffPoint: DropOffPointModel) => {
    setSelectedPoint(nearbyDropOffPoint)
  }

  const renderAddressMarker = () => {
    if (!addressCoordinates) return null

    return <AddressMarker lat={addressCoordinates.latitude} lng={addressCoordinates.longitude} />
  }

  const renderCurrentLocationMarker = () => {
    if (!location) return null

    return <CurrentLocation lat={location.latitude} lng={location.longitude} />
  }

  const renderMarkers = () => {
    return nearbyDropOffPoints?.map((nearbyDropOffPoint: DropOffPointModel, index: number) => {
      return (
        <DropOffPointMarker
          key={`${nearbyDropOffPoint.code}_${index}`}
          lat={Number(nearbyDropOffPoint.latitude)}
          lng={Number(nearbyDropOffPoint.longitude)}
          dropOffPoint={nearbyDropOffPoint}
          isActive={nearbyDropOffPoint?.code === selectedDropOffPoint?.code}
          isMouseOverListElement={nearbyDropOffPoint?.code === nearbyDropOffPoint.code}
          onClick={handleMarkerClick}
          id={index}
        />
      )
    })
  }

  if (!countryBounds) return null

  return (
    <div className="u-position-relative u-fill-height u-fill-width">
      <div className="drop-off-point-map">
        <GoogleMap
          googleApiKey={googleApiKey}
          defaultCenter={{
            lat: countryBounds.center.latitude || 0,
            lng: countryBounds.center.longitude || 0,
          }}
          defaultZoom={MAP_DEFAULT_ZOOM}
          mapOptions={{
            disableDefaultUI: true,
            clickableIcons: false,
            minZoom: MAP_MIN_ZOOM,
            gestureHandling: 'greedy',
            scaleControl: true,
          }}
          onMapLoaded={handleMapLoaded}
          useGoogleInternalApi
        >
          {renderAddressMarker()}
          {renderCurrentLocationMarker()}
          {renderMarkers()}
        </GoogleMap>
      </div>
      <Controls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onLocationResponse={handleLocationResponse}
      />
      <SearchArea show={showSearchArea} uiState={uiState} onClick={handleSearchAreaClick} />
      {uiState === UiState.Pending && (
        <div className="drop-off-point-map__loader">
          <Loader
            size={Loader.Size.Large}
            styling={Loader.Styling.Lifted}
            testId="drop-off-point-map-loader"
          />
        </div>
      )}
    </div>
  )
}

export default DropOffPointMap
